import { Clock, CircleDollarSign, LogOut, Menu, Moon, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/auth-context";
import { DEFAULT_CLIENT_ROUTE, isNavigationItemActive, navigationItems } from "@/lib/navigation";
import { getUserFacingError } from "@/lib/api/errors";
import { lockBodyScroll } from "@/lib/scroll-lock";
import { api } from "@/lib/api/services";
import { pendingWithdrawalTotal } from "@/lib/finance";
import { formatMoney } from "@/lib/formatters";
import { useApiResource } from "@/lib/hooks/use-api-resource";
import { useT } from "@/lib/i18n";
import { applyColorTheme, readColorTheme } from "@/lib/color-theme";
import { AccountChip } from "./account-menu";
import { ActivationGate } from "./activation-gate";
import { SupportFab } from "./support-fab";
import { Brand } from "./brand";
import { MarketplaceSearch } from "./marketplace-search";
import { NotificationsMenu } from "./notifications-menu";
import { OnboardingGate } from "./onboarding-gate";
import { PopupNotices } from "./popup-notices";
import { SiteFooter } from "./site-footer";

export function ClientLayout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const [logoutPending, setLogoutPending] = useState(false);
  const [logoutError, setLogoutError] = useState<unknown>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // Any navigation (link, search result, back button) closes the phone menu.
  const [menuPath, setMenuPath] = useState(location.pathname);
  if (menuPath !== location.pathname) {
    setMenuPath(location.pathname);
    setMenuOpen(false);
  }
  const [theme, setTheme] = useState(readColorTheme);
  const mobileMenuRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const isActiveUser = auth.status === "authenticated" && auth.user.active;
  const history = useApiResource(isActiveUser ? "header-finance-history" : null, (signal) => api.getFinanceHistory(signal));

  useEffect(() => {
    applyColorTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (!menuOpen) return;
    const unlock = lockBodyScroll();
    const triggerButton = menuButtonRef.current;
    const focusable = mobileMenuRef.current ? Array.from(mobileMenuRef.current.querySelectorAll<HTMLElement>("a, button")) : [];
    focusable[0]?.focus();

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => {
      unlock();
      window.removeEventListener("keydown", handleKeydown);
      triggerButton?.focus();
    };
  }, [menuOpen]);

  async function logout() {
    if (logoutPending) return;
    setLogoutPending(true); setLogoutError(null);
    try {
      await auth.logout();
      await navigate("/auth/login", { replace: true, viewTransition: true });
    } catch (error) { setLogoutError(error); }
    finally { setLogoutPending(false); }
  }

  function toggleTheme() {
    setTheme((value) => (value === "dark" ? "light" : "dark"));
  }

  if (auth.status !== "authenticated") return null;
  if (!auth.user.active) return <ActivationGate />;
  const currentUser = auth.user;
  const pendingWithdrawal = history.status === "success" ? pendingWithdrawalTotal(history.data, currentUser.currency) : 0;

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__inner">
          <Brand to={DEFAULT_CLIENT_ROUTE} />
          <nav className="main-nav" aria-label={t("auth.main_nav")}>
            {navigationItems.map((item) => <Link viewTransition to={item.to} key={item.to} className={isNavigationItemActive(item, location.pathname) ? "active" : undefined} aria-current={isNavigationItemActive(item, location.pathname) ? "page" : undefined}>{t(item.labelKey)}</Link>)}
          </nav>
          <div className="header-right">
            <MarketplaceSearch />
            <div className="header-actions">
              <Link viewTransition to="/client/finance?tab=topup" className="balance-chip" title={t("header.balance_title")}><CircleDollarSign size={16} /><span>{formatMoney(currentUser.balance, currentUser.currency)}</span></Link>
              {pendingWithdrawal > 0 ? <span className="balance-chip balance-chip--pending" title={t("header.pending_title")}><Clock size={16} /><span>{formatMoney(pendingWithdrawal, currentUser.currency)}</span></span> : null}
              <button type="button" className="icon-button header-theme" aria-label={t("header.theme_toggle")} title={t("header.theme_toggle")} onClick={toggleTheme}><Moon size={19} /></button>
              <NotificationsMenu />
              <AccountChip user={currentUser} />
              <button type="button" className="icon-button header-logout" disabled={logoutPending} aria-label={t("header.logout")} title={t("header.logout")} onClick={() => void logout()}><LogOut size={19} /></button>
              <button ref={menuButtonRef} className="menu-button" type="button" aria-expanded={menuOpen} aria-controls="mobile-nav" aria-label={menuOpen ? t("header.close_menu") : t("header.open_menu")} onClick={() => setMenuOpen((value) => !value)}>{menuOpen ? <X /> : <Menu />}</button>
            </div>
          </div>
        </div>
        {menuOpen ? (
          <nav id="mobile-nav" className="mobile-menu container" aria-label={t("auth.mobile_nav")} ref={mobileMenuRef}>
            {navigationItems.map((item) => <Link viewTransition to={item.to} key={item.to} className={isNavigationItemActive(item, location.pathname) ? "active" : undefined} onClick={() => setMenuOpen(false)}><item.icon size={18} aria-hidden="true" /> {t(item.labelKey)}</Link>)}
            <button type="button" onClick={toggleTheme}><Moon size={18} aria-hidden="true" /> {t("header.theme_toggle")}</button>
            <button type="button" disabled={logoutPending} onClick={() => void logout()}><LogOut size={18} aria-hidden="true" /> {t("header.logout")}</button>
          </nav>
        ) : null}
      </header>
      {logoutError ? <div className="container inline-alert" role="alert">{getUserFacingError(logoutError)}</div> : null}
      <div className="app-shell__main"><Outlet /></div>
      <SiteFooter />
      <SupportFab />
      <OnboardingGate />
      <PopupNotices />
    </div>
  );
}
